import type { FC } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '@/components/hooks';
import { getErrorResponse } from '@/components/utils';
import LoadErrorReportPage from '@/components/Common/LoadErrorReport/LoadErrorReportPage';
import { buildErrorDiagnostics } from '@/components/Common/LoadErrorReport/buildErrorDiagnostics';
import { HELPDESK_EMAILS_MAILTO } from '@/config/support';

import {
    ErrorResult, getDefaultErrorResultProps,
} from '../../../Common';
import SkeletonView from '../SkeletonView';
import LinkView from '../LinkView';

type ContentType = 'errorResult' | 'skeletonView' | 'linkView';

const getContentType = (errorType: string, showSkeleton: boolean): ContentType => {
    let contentType: ContentType = 'skeletonView';

    if (errorType !== null) {
        contentType = 'errorResult';
    } else if (!showSkeleton) {
        contentType = 'linkView';
    }

    return contentType;
};

/** Mirrors SharedFilesStore.fetchFiles' id resolution so diagnostics stay accurate. */
const resolveLinkFileIds = (linkData: { files_ids?: string[]; file_id?: string; message_id?: string }): string[] => {
    if (!linkData) {
        return [];
    }
    let fileIds: string[] = linkData.files_ids ?? [];
    if (!fileIds.length) {
        fileIds = linkData.file_id ? [linkData.file_id] : [];
    }
    if (fileIds.length && !fileIds[0] && linkData.message_id) {
        fileIds = [linkData.message_id];
    }
    return fileIds.filter(Boolean);
};

interface RecipientPageContentProps {
    isLanguageDefined: boolean;
}

const RecipientPageContent: FC<RecipientPageContentProps> = observer(({ isLanguageDefined }) => {
    const { recipientPageStore, userStore } = useStores();
    const { t } = useTranslation();
    const { sharedFilesStore } = recipientPageStore;

    const {
        errorType,
        linkData,
        linkDebugInfo,
        linkErrorObject,
        linkId,
        tryFetchLink,
    } = sharedFilesStore;

    const showSkeleton = !isLanguageDefined;

    const contentType = getContentType(errorType, showSkeleton);

    const statusCode = getErrorResponse(linkErrorObject).statusCode;
    const fileIds = resolveLinkFileIds(linkData);
    const isPreparingFile = errorType === 'serverError' && statusCode === 408;

    const diagnostics = buildErrorDiagnostics({
        error: linkErrorObject ?? undefined,
        fileIds,
        linkId,
        user: userStore.currentUserEmail,
        userId: userStore.currentUserId,
    });

    const linkErrorTitle = t('general.specterxCommon.couldNotLoadItemTryAgain', {
        item: t('general.specterxCommon.link'),
    });

    const loadErrorScreen = (
        <LoadErrorReportPage
            variant={isPreparingFile ? '408' : 'generic'}
            fileId={isPreparingFile ? fileIds[0] : undefined}
            compact
            diagnostics={diagnostics}
            helpdeskRecipients={HELPDESK_EMAILS_MAILTO}
            genericTitle={linkErrorTitle}
            onPreparationRetry={isPreparingFile ? () => tryFetchLink(true) : undefined}
        />
    );

    const errorResultContent = (
        <div style={{ flex: 1, overflowY: 'auto' }}>
            {errorType === 'serverError'
                ? loadErrorScreen
                : (
                    <ErrorResult
                        {...getDefaultErrorResultProps(
                            t,
                            errorType,
                            'general.specterxCommon.link',
                            linkData?.src_email,
                            linkDebugInfo,
                        )}
                    />
                )}
        </div>
    );

    return ({
        errorResult: errorResultContent,
        skeletonView: <SkeletonView />,
        linkView: <LinkView />,
    }[contentType]);
});

export default RecipientPageContent;
